import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder } from '@angular/forms';
import { PlantService } from '@app/core/services/plant.service';
import { Plant } from '@app/shared/models/plant.model';
import { Line } from '@app/shared/models/line.model';

import * as _ from 'lodash';
import { PermissionService } from '@app/authentication/services/permission.service';

@Component({
    selector: "app-line-details-form",
    templateUrl: "./line-details-form.component.html",
    styleUrls: ["./line-details-form.component.scss"],
})
export class LineDetailsFormComponent implements OnInit {
    _logger = Log.create(LineDetailsFormComponent.name);

    @Input() showValidations = false;
    @Input() isEditMode: boolean;
    @Output() saveDetailsFromData = new EventEmitter<any>();
    @Output() spiValueChanged = new EventEmitter<any>();

    public line: Line;
    public detailsForm: UntypedFormGroup;
    public plants: Plant[];

    get plant() {
        return this.line.plant || {id: 0};
    }

    get isFormValid() {
        return this.detailsForm.valid;
    }

    constructor(
        private plantService: PlantService,
        private permissionService: PermissionService,
        private formBuilder: UntypedFormBuilder
    ) {
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing line details form");
        this.line = new Line();

        const emailPattern = "[a-zA-Z0-9._-]+[@]+[a-zA-Z0-9-]+[.]+[a-zA-Z]{2,10}";

        this.detailsForm = this.formBuilder.group({
            name: new UntypedFormControl("", Validators.required),
            plant: new UntypedFormControl("", Validators.required),
            mesId: new UntypedFormControl("", Validators.required),
            description: new UntypedFormControl("", Validators.required),
            isSPI: new UntypedFormControl(false),
            toolRequestsLimit: new UntypedFormControl("", [
                Validators.required, Validators.min(0)
            ]),
            contactEmailAddress: new UntypedFormControl("", [
                Validators.pattern(emailPattern)
            ])
        });

        const isSPIControl = this.detailsForm.get("isSPI");
        isSPIControl.valueChanges.subscribe((val) => {
            if (val === null) {
                return;
            }
            this.spiValueChanged.emit(val);
        });
    }

    async init(line: Line) {
        if (this.shouldRenderModifiers()) {
            await this.getPlants();
        }

        if (line) {
            this.line = line;
            this.loadFormValue();
        }
    }

    private loadFormValue() {
    if (!this.line) {
        return;
    }

    if (this.line.plant && this.line.plant.id === 0) {
        this.line.plant = null;
    }

    const {
        name,
        plant,
        mesId,
        description,
        isSPI,
        toolRequestsLimit,
        contactEmailAddress
    } = this.line;

    this.detailsForm.setValue({
        name,
        plant,
        mesId,
        description,
        isSPI,
        toolRequestsLimit,
        contactEmailAddress
    });
}

    resetForm() {
        this._logger.info("resetForm:: Resetting line details form", this.line);
        this.detailsForm.reset();
    }

    getFormDetails() {
        this._logger.info("getFormDetails::", this.line);
        const formData = this.detailsForm.getRawValue();
        formData.plantId = formData.plant ? formData.plant.id : 0;
        return formData;
    }

    private async getPlants() {
        this._logger.info("getPlants:: Retrieving list of plants");
        try {
            const data = await this.plantService.getAllPlants().toPromise();
            this._logger.info("getPlants:: API returned data", data);
            this.plants = data.items || [];
        } catch (error) {
            this._logger.error("getPlants:: API returned error", error);
            this._logger.error(error);
        }
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature(
            "MANAGE_LINES"
        );
    }
}
